<template>
  <div class="collapse-sidebar pt-[18px] pb-2 pl-4">
    <div class="relative w-full mb-[10px]">
      <img
        :src="minLogoSrc"
        alt=""
        height="48"
        width="40"
        class="cursor-pointer object-cover ml-1"
        data-testid="collapsed-home-logo-button"
        @click="handleFaviconClick"
      />
      <div
        class="absolute top-1/2 -translate-y-1/2 -right-[12px] z-10 fc rounded-[6px] cursor-pointer bg-container border border-border-secondary"
        data-testid="expand-product-sidebar-button"
        @click="handleExpandSidebar"
      >
        <BotIcon name="expand" size="24" :padding="0" />
      </div>
    </div>
    <div
      v-if="(isHomeOwner && hasHomeland) || isCompanyUser"
      class="w-full mb-[8px] pl-1"
    >
      <CreateAgent is-min />
    </div>
    <u-scrollbar class="px-1 collapse-scrollbar">
      <div>
        <template v-if="!isCompanyUser">
          <div class="border-t w-[40px] h-[1px] mb-4 mt-[17px]" />
          <div
            class="menu-item fc w-[40px] h-[32px] rounded-[6px] mb-[12px]"
            :class="{
              'active-route': routeName === 'agentStore'
            }"
            data-testid="open-agent-store-menu"
            @click="handleNavigate('agentStore')"
          >
            <BotIcon name="planet-line" size="16" :padding="0" />
          </div>
        </template>

        <div v-if="isCompanyUser" class="flex flex-col">
          <div class="border-t w-[40px] h-[1px] mb-[16px] mt-[9px]" />

          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
            data-testid="collapsed-enterprise-workshop-menu-button"
            :class="{
              'active-route': routeName === 'enterprise'
            }"
            @click="handleNavigate('enterprise')"
          >
            <BotIcon name="building-line" size="16" :padding="0" />
          </div>
          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
            data-testid="collapsed-agent-monitor-menu-button"
            :class="{
              'active-route': routeName === 'agentMonitor'
            }"
            @click="handleNavigate('agentMonitor')"
          >
            <BotIcon name="speed-up-fill" size="16" :padding="0" />
          </div>
          <div
            v-permission="'appproval_management_menu'"
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mt-[4px]"
            :class="{
              'active-route': routeName === 'enterpriseApproval'
            }"
            data-testid="enterprise-approval-collapse-menu-button"
            @click="handleNavigate('enterpriseApproval')"
          >
            <BotIcon name="audit" size="16" :padding="0" />
          </div>
          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px]"
            data-testid="collapsed-agent-management-menu-button"
            :class="{
              'active-route': [
                'agentManagement',
                'agentManagementAgentAnalysis'
              ].includes(routeName)
            }"
            @click="handleNavigate('agentManagement')"
          >
            <BotIcon name="pie-chart-line" size="16" :padding="0" />
          </div>
        </div>

        <!-- Super entry: keep the same order as the expanded sidebar -->
        <div
          v-if="showSuperEntry || showBatchTask"
          class="border-t w-[40px] h-[1px] mt-4 pt-4"
        />
        <div
          v-if="showSuperEntry"
          class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
          :class="{
            'active-route':
              typeof routeName === 'string' &&
              routeName.startsWith('superEntry')
          }"
          data-testid="open-super-entry-menu"
          @click="handleNavigate(SUPER_ENTRY_ROUTE)"
        >
          <BotIcon name="super-entry" size="16" :padding="0" />
        </div>
        <!-- Batch task: below the super entry for both personal and company users -->
        <div
          v-if="showBatchTask"
          class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
          :class="{
            'active-route': routeName === 'batchTask'
          }"
          data-testid="open-batch-task-menu"
          @click="handleNavigate('batchTask')"
        >
          <BotIcon name="batch" size="16" :padding="0" />
        </div>
        <div class="border-t w-[40px] h-[1px] my-4" />
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-workspace-menu-button"
          :class="{
            'active-route': ['workspace', 'workspaceAgentAnalysis'].includes(
              routeName
            )
          }"
          @click="handleNavigate('workspace')"
        >
          <BotIcon name="ic-workbench" size="16" :padding="0" />
        </div>
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-resource-menu-button"
          :class="{
            'active-route': [
              'resource',
              'pluginTool',
              'ideTool',
              'voiceprintDetail'
            ].includes(routeName)
          }"
          @click="
            handleNavigate('resource', { space: 'space', tab: 'knowledge' })
          "
        >
          <BotIcon name="resources" size="16" :padding="0" />
        </div>
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] mt-[4px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-agent-evaluation-menu-button"
          :class="{
            'active-route': routeName === 'agentEvaluation'
          }"
          @click="handleNavigate('agentEvaluation')"
        >
          <BotIcon name="pen" size="16" :padding="0" />
        </div>
        <template v-if="showHardwareMenu">
          <div class="border-t w-[40px] h-[1px] mt-[18px] mb-[16px]" />
          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
            data-testid="collapsed-hardware-product-menu-button"
            :class="{
              'active-route': [
                'hardwareProduct',
                'hardwareProductDetail'
              ].includes(routeName)
            }"
            @click="handleNavigate('hardware/product')"
          >
            <BotIcon
              name="device-line"
              size="16"
              :padding="0"
              :tip="
                $t(
                  'views.layout.components.CollapseSidebar.658f6167f279701e8fbd43dba43e6e77'
                )
              "
            />
          </div>
          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
            data-testid="collapsed-hardware-firmware-menu-button"
            :class="{
              'active-route': routeName === 'hardwareFirmware'
            }"
            @click="handleNavigate('hardware/firmware')"
          >
            <BotIcon
              name="equipment-upgrade"
              size="16"
              :padding="0"
              :tip="
                $t(
                  'views.layout.components.CollapseSidebar.970d5d8b370bcdb502d2fc71a6a21439'
                )
              "
            />
          </div>
          <div
            class="menu-item fc w-[40px] h-[32px] hover-bg rounded-[6px] mb-[4px]"
            data-testid="collapsed-hardware-logs-menu-button"
            :class="{
              'active-route': routeName === 'hardwareLogs'
            }"
            @click="handleNavigate('hardware/logs')"
          >
            <BotIcon
              name="device-log"
              size="16"
              :padding="0"
              :tip="
                $t(
                  'views.layout.components.CollapseSidebar.bfaba614f89f32da15bae949e5ff78b0'
                )
              "
            />
          </div>
        </template>
        <div class="border-t w-[40px] h-[1px] my-4" />
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] mt-[4px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-publish-manage-menu-button"
          :class="{
            'active-route': [
              'publishManage',
              'publishManageAgentAnalysis'
            ].includes(routeName)
          }"
          @click="handleNavigate('publishManage')"
        >
          <BotIcon name="send-plane-line" size="16" :padding="0" />
        </div>
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] mt-[4px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-observability-menu-button"
          :class="{
            'active-route': routeName === 'agentOpsObservability'
          }"
          @click="handleNavigate('observability')"
        >
          <BotIcon name="line-chart-line" size="16" :padding="0" />
        </div>
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] mt-[4px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-auto-tuning-menu-button"
          :class="{
            'active-route': routeName === 'agentOpsAutoTuning'
          }"
          @click="handleNavigate('autoTuning')"
        >
          <BotIcon name="auto-line" size="16" :padding="0" />
        </div>
        <div class="border-t w-[40px] h-[1px] my-4" />
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] mt-[4px] hover-bg rounded-[6px] mb-1"
          data-testid="collapsed-space-settings-menu-button"
          :class="{
            'active-route': routeName === 'homeland'
          }"
          @click="handleNavigate('homeland')"
        >
          <BotIcon name="settings" size="16" :padding="0" />
        </div>
        <div
          v-if="isCompanyUser"
          class="menu-item flex flex-col items-center justify-center w-[40px] h-[32px] mb-1 hover-bg rounded-[6px]"
          data-testid="collapsed-log-audit-menu-button"
          :class="{
            'active-route': routeName === 'logAudit'
          }"
          @click="handleNavigate('logAudit')"
        >
          <BotIcon name="log-line" size="16" :padding="0" />
        </div>
        <div
          class="menu-item flex flex-col items-center justify-center w-[40px] h-[32px] mb-[18px] hover-bg rounded-[6px]"
          data-testid="collapsed-documentation-menu-button"
          :class="{
            'active-route': isDocCenterActive
          }"
          @click="handleDocCenter"
        >
          <BotIcon name="resources" size="16" :padding="0" />
        </div>
      </div>
    </u-scrollbar>
    <div class="flex flex-col mr-4 items-center">
      <UserMenu
        placement="top-start"
        :offset="[0, 8]"
        :disabled="spaceMenuVisible"
      >
        <div
          class="menu-item flex items-center justify-center w-[40px] h-[32px] hover-bg rounded-[6px]"
          data-testid="open-user-menu-button"
        >
          <BotIcon name="manu-meta-setting" size="16" :padding="0" />
        </div>
      </UserMenu>
      <div
        class="centered-flex-container w-full mt-2"
        :class="{ 'space-active': spaceMenuVisible }"
      >
        <ChangeSpace
          is-min
          :style="changeSpaceStyle"
          @update:visible="val => (spaceMenuVisible = val)"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { faviconSrc, minLogoSrc } from '@/libs/img';
import { useRouter } from 'vue-router';
import CreateAgent from './CreateAgent.vue';
import ChangeSpace from './ChangeSpace.vue';
import UserMenu from './UserMenu.vue';
import isBaiyuHardwareEnabled, {
  baiyuHardwareMenuEnabled
} from '@libs/isBaiyuHardwareEnabled';
import useSuperEntryAvailability from '@/hooks/useSuperEntryAvailability';
export default defineComponent({
  name: 'CollapseSidebar',
  components: {
    CreateAgent,
    ChangeSpace,
    UserMenu
  },
  props: {
    routeName: {
      type: String,
      default: ''
    },
    src: {
      type: String,
      default: ''
    },
    showBatchTask: {
      type: Boolean,
      default: false
    },
    showSuperEntry: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:showCollapseSidebar'],
  setup(_, { emit }) {
    const spaceMenuVisible = ref(false);
    const store = useStore();
    const router = useRouter();
    const homelandInfo = computed(() => {
      return store.getters.homelandInfo || {};
    });
    const isHomeOwner = computed(() => {
      return homelandInfo.value.role === 'home_owner';
    });
    const hasHomeland = computed(() => {
      return homelandInfo.value.homeland;
    });
    const isCompanyUser = computed(() => store.getters.isCompanyUser);
    const { hideTempSidebarEntries } = useSuperEntryAvailability();
    const SUPER_ENTRY_ROUTE = computed(() =>
      hideTempSidebarEntries.value ? 'composer/vibe' : 'composer/session'
    );
    const handleFaviconClick = () => {
      router.push('/');
    };
    const handleNavigate = (route, query = {}) => {
      router.push({
        path: `/${route}`,
        query
      });
    };
    const handleDocCenter = () => {
      window.open('https://agentstudio.gitbook.io/agentstudio', '_blank');
    };
    const isDocCenterActive = computed(() => {
      const name = router.currentRoute.value.name;
      return (
        typeof name === 'string' &&
        (name.startsWith('docCenter') || name.startsWith('document'))
      );
    });
    const handleExpandSidebar = () => {
      emit('update:showCollapseSidebar');
    };
    const changeSpaceStyle = `padding: 0 6px 0 8px;border-radius: 6px;width: 100%;`;
    const showHardwareMenu = computed(
      () => baiyuHardwareMenuEnabled.value || isBaiyuHardwareEnabled()
    );
    return {
      SUPER_ENTRY_ROUTE,
      faviconSrc,
      handleFaviconClick,
      handleNavigate,
      handleDocCenter,
      isDocCenterActive,
      isHomeOwner,
      isCompanyUser,
      minLogoSrc,
      hasHomeland,
      handleExpandSidebar,
      spaceMenuVisible,
      changeSpaceStyle,
      showHardwareMenu
    };
  }
});
</script>
<style scoped lang="less">
.collapse-sidebar {
  height: 100%;
  width: 100%;
  color: var(--color-text-icon-body-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.active-route {
  background-color: var(--background-neutral-bg-color-elevated) !important;
  color: var(--text-primary-color-primary-text);
}
.menu-item {
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background-color: var(--background-fill-white-color-fill-secondary);
  }
}
.centered-flex-container {
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background-color: var(--background-fill-white-color-fill-secondary);
  }
}
.space-active {
  background-color: var(--background-neutral-bg-color-elevated);
  border-radius: 6px;
}

.collapse-scrollbar {
  flex: 1;
  min-height: 0;
  width: 100%;
  :deep(.uui-scrollbar) {
    height: 100%;
  }
  :deep(.uui-scrollbar__wrap) {
    height: 100%;
  }
}
.coll-sidebar-expand-btn {
  position: relative;
  margin-left: -8px;
}
</style>
